import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaExternalLinkAlt, FaGithub, FaNewspaper } from 'react-icons/fa';
import { projects } from '../content/data';

const categories = ['All', 'ML', 'Full-stack', 'Mobile'];

const ProjectsGrid = ({ onProjectClick }) => {
  const [activeFilter, setActiveFilter] = useState('All');

  const filteredProjects = useMemo(() => {
    const filtered = activeFilter === 'All' 
      ? projects 
      : projects.filter((p) => p.category.includes(activeFilter));
    
    return filtered.sort((a, b) => a.id.localeCompare(b.id));
  }, [activeFilter]);

  return (
    <section id="projects" className="section-padding">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-8"
        >
          <h2 className="text-h1 gradient-text mb-3">All Projects</h2>
          <p className="text-muted text-body-lg">Things I've built</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="flex flex-wrap justify-center gap-2 mb-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveFilter(category)}
              className={`chip cursor-pointer ${
                activeFilter === category ? 'chip-active' : ''
              }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="grid md:grid-cols-2 gap-6"
          >
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="card group cursor-pointer flex flex-col"
                onClick={() => onProjectClick(project)}
              >
                <div className="aspect-[16/10] overflow-hidden rounded-t-card">
                  <img
                    src={`/projects/${project.image}`}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex flex-col flex-grow">
                  <h3 className="text-h3 text-white mb-1 group-hover:text-accent-blue transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-muted text-caption mb-3">{project.tagline}</p>

                  <div className="flex flex-wrap gap-1.5 mb-3">
                    {project.stack.slice(0, 5).map((tech) => (
                      <span key={tech} className="chip text-xs py-0.5 px-2">
                        {tech}
                      </span>
                    ))}
                    {project.stack.length > 5 && (
                      <span className="chip text-xs py-0.5 px-2">
                        +{project.stack.length - 5}
                      </span>
                    )}
                  </div>

                  <ul className="text-muted text-caption space-y-1 mb-4 flex-grow">
                    {project.impact.slice(0, 2).map((item, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <span className="text-accent-cyan mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex gap-2 mt-auto">
                    {project.links.demo && (
                      <a
                        href={project.links.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary text-xs py-2 px-3 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaExternalLinkAlt className="text-[10px]" />
                        Demo
                      </a>
                    )}
                    {project.links.code && (
                      <a
                        href={project.links.code}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-xs py-2 px-3 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaGithub />
                        Code
                      </a>
                    )}
                    {project.links.caseStudy && (
                      <a
                        href={project.links.caseStudy}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary text-xs py-2 px-3 flex items-center gap-1"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <FaNewspaper />
                        Article
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ProjectsGrid;
